import { FC, MouseEvent } from 'react'
import cn from 'classnames'
import { LockOutlined } from '@ant-design/icons'
import type { CrmChatProviderKey } from '@/api/types/crm/crmLeadFunnels.types'
import type { IntegrationItem } from '@/api/integrations/integrations.types'
import { CRM_INTEGRATION_CHAT_PROVIDERS } from '@pages/Admin/CRM/components/PhoneIntegrationButtons/integrations'
import WazzupIcon from '@pages/Admin/CRM/components/PhoneIntegrationButtons/WazzupIcon'
import VkUnreadCountBadge from '@pages/Admin/CRM/components/integrations/vk/components/VkUnreadCountBadge'
import { formatCrmChatUnreadBadge } from '@pages/Admin/CRM/utils/leadFields'
import type { CrmIntegrationChatAvailabilityMap } from '@pages/Admin/CRM/utils/crmIntegrationChatAvailability'
import { isIntegrationCrmAccessible } from '@pages/Admin/CRM/utils/crmIntegrationAccess'
import { CompressIcon } from '@/utils/icons'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import {
  CRM_CHAT_UNREAD_ALL_TOOLTIP,
  CRM_CHAT_UNREAD_MINE_TOOLTIP,
  type IntegrationChatUnreadByIntegrationId,
} from './integrationChatGlobalUnread'
import classes from './IntegrationChatDock.module.css'

type IntegrationChatDockIntegrationsPickerProps = {
  providers: CrmIntegrationChatAvailabilityMap
  providerUnreadCounts: Record<CrmChatProviderKey, number>
  providerMyUnreadCounts?: Record<CrmChatProviderKey, number>
  wazzupAccounts?: IntegrationItem[]
  wazzupAccountsLoading?: boolean
  unreadByIntegrationId?: IntegrationChatUnreadByIntegrationId
  isLoading?: boolean
  onCollapse?: () => void
  onSelectProvider: (provider: CrmChatProviderKey) => void
  onSelectWazzupAccount: (account: IntegrationItem) => void
}

const formatPickerUnreadHint = (count: number): string | null => {
  if (!Number.isFinite(count) || count <= 0) {
    return null
  }

  const normalized = Math.floor(count)
  if (normalized === 1) {
    return '1 непрочитанное'
  }

  return `${normalized > 99 ? '99+' : normalized} непрочитанных`
}

const formatPickerMyUnreadHint = (count: number): string | null => {
  if (!Number.isFinite(count) || count <= 0) {
    return null
  }

  const normalized = Math.floor(count)
  if (normalized === 1) {
    return '1 моё'
  }

  return `${normalized > 99 ? '99+' : normalized} моих`
}

const getWazzupAccountTitle = (account: IntegrationItem): string => {
  const name = account.name?.trim()
  return name || `Аккаунт #${account.id}`
}

const stopBadgeEvent = (event: MouseEvent) => {
  event.stopPropagation()
}

const IntegrationChatDockIntegrationsPicker: FC<
  IntegrationChatDockIntegrationsPickerProps
> = ({
  providers,
  providerUnreadCounts,
  providerMyUnreadCounts,
  wazzupAccounts = [],
  wazzupAccountsLoading = false,
  unreadByIntegrationId = {},
  isLoading = false,
  onCollapse,
  onSelectProvider,
  onSelectWazzupAccount,
}) => {
  const nonWazzupProviders = CRM_INTEGRATION_CHAT_PROVIDERS.filter(
    (item) => item.provider !== 'wazzup',
  )
  const wazzupAvailability = providers.wazzup
  const showWazzupAccounts =
    wazzupAvailability.isConnected || wazzupAccounts.length > 0

  return (
    <div className={classes.pickerRoot}>
      <header className={classes.pickerHeader}>
        <div className={classes.pickerHeaderMain}>
          <span className={classes.pickerTitle}>Чаты интеграций</span>
          <span className={classes.pickerSubtitle}>
            Выберите мессенджер для просмотра диалогов
          </span>
        </div>
        {onCollapse ? (
          <button
            type="button"
            className={classes.pickerCollapseButton}
            aria-label="Свернуть чат"
            onClick={onCollapse}
          >
            <CompressIcon />
          </button>
        ) : null}
      </header>

      <div className={classes.pickerGrid}>
        {nonWazzupProviders.map(({ provider, icon: Icon, label, color }) => {
          const availability = providers[provider]
          const isConnected = availability.isConnected
          const canUseLeadChat = availability.canUseLeadChat
          const canOpenProvider = isConnected
          const isDisabled = isLoading || !canOpenProvider
          const statusHint = !isConnected
            ? 'Не подключено'
            : !canUseLeadChat
              ? 'Нет доступа'
              : null
          const unreadCount = providerUnreadCounts[provider] ?? 0
          const myUnreadCount = providerMyUnreadCounts?.[provider] ?? 0
          const unreadLabel =
            canUseLeadChat && unreadCount > 0
              ? formatCrmChatUnreadBadge(unreadCount)
              : ''
          const myUnreadLabel =
            canUseLeadChat && myUnreadCount > 0
              ? formatCrmChatUnreadBadge(myUnreadCount)
              : ''
          const unreadHint = canUseLeadChat
            ? formatPickerUnreadHint(unreadCount)
            : null
          const myUnreadHint = canUseLeadChat
            ? formatPickerMyUnreadHint(myUnreadCount)
            : null
          const ariaParts = [
            label,
            statusHint,
            unreadHint,
            myUnreadHint,
          ].filter(Boolean)

          return (
            <button
              key={provider}
              type="button"
              className={cn(classes.pickerCard, {
                [classes.pickerCard_disabled]: isDisabled,
                [classes.pickerCard_hasUnread]: Boolean(
                  unreadLabel || myUnreadLabel,
                ),
              })}
              disabled={isDisabled}
              aria-disabled={isDisabled}
              aria-label={ariaParts.join(', ')}
              onClick={() => {
                if (!canOpenProvider) return
                onSelectProvider(provider)
              }}
            >
              {unreadLabel || myUnreadLabel ? (
                <span
                  className={classes.pickerUnreadBadges}
                  onMouseDown={stopBadgeEvent}
                  onClick={stopBadgeEvent}
                >
                  {unreadLabel ? (
                    <TooltipWrapper title={CRM_CHAT_UNREAD_ALL_TOOLTIP}>
                      <span className={classes.unreadBadgeHit}>
                        <VkUnreadCountBadge
                          label={unreadLabel}
                          placement="inline"
                          dock
                          variant="all"
                          aria-label={`${CRM_CHAT_UNREAD_ALL_TOOLTIP}: ${unreadCount}`}
                        />
                      </span>
                    </TooltipWrapper>
                  ) : null}
                  {myUnreadLabel ? (
                    <TooltipWrapper title={CRM_CHAT_UNREAD_MINE_TOOLTIP}>
                      <span className={classes.unreadBadgeHit}>
                        <VkUnreadCountBadge
                          label={myUnreadLabel}
                          placement="inline"
                          dock
                          variant="mine"
                          aria-label={`${CRM_CHAT_UNREAD_MINE_TOOLTIP}: ${myUnreadCount}`}
                        />
                      </span>
                    </TooltipWrapper>
                  ) : null}
                </span>
              ) : null}
              <span
                className={classes.pickerCardIconWrap}
                style={{ color: canOpenProvider ? color : undefined }}
              >
                <Icon className={classes.pickerCardIcon} />
              </span>
              <span className={classes.pickerCardLabel} title={label}>
                {label}
              </span>
              {unreadHint || myUnreadHint ? (
                <span className={classes.pickerCardUnreadHints}>
                  {unreadHint ? (
                    <span className={classes.pickerCardUnreadHint}>
                      {unreadHint}
                    </span>
                  ) : null}
                  {myUnreadHint ? (
                    <span
                      className={cn(
                        classes.pickerCardUnreadHint,
                        classes.pickerCardUnreadHint_mine,
                      )}
                    >
                      {myUnreadHint}
                    </span>
                  ) : null}
                </span>
              ) : null}
              {statusHint ? (
                <span className={classes.pickerCardHint}>
                  <LockOutlined
                    className={classes.pickerCardLock}
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                  {statusHint}
                </span>
              ) : null}
            </button>
          )
        })}

        {showWazzupAccounts && wazzupAccountsLoading && wazzupAccounts.length === 0
          ? (
            <div className={classes.pickerCard} aria-busy="true">
              <span
                className={classes.pickerCardIconWrap}
                style={{ color: 'var(--color-status-success-text)' }}
              >
                <WazzupIcon className={classes.pickerCardIcon} />
              </span>
              <span className={classes.pickerCardLabel} title="Wazzup">
                Wazzup
              </span>
              <span className={classes.pickerCardHint}>Загрузка…</span>
            </div>
          )
          : null}

        {showWazzupAccounts
          ? wazzupAccounts.map((account) => {
              const title = getWazzupAccountTitle(account)
              const isAccessible = isIntegrationCrmAccessible(account)
              const statusHint = isAccessible ? null : 'Нет доступа'
              const unreadCount = unreadByIntegrationId[account.id] ?? 0
              const unreadLabel =
                isAccessible && unreadCount > 0
                  ? formatCrmChatUnreadBadge(unreadCount)
                  : ''
              const unreadHint = isAccessible
                ? formatPickerUnreadHint(unreadCount)
                : null
              const ariaParts = [title, 'Wazzup', statusHint, unreadHint].filter(
                Boolean,
              )

              return (
                <button
                  key={`wazzup-${account.id}`}
                  type="button"
                  className={cn(classes.pickerCard, {
                    [classes.pickerCard_hasUnread]: Boolean(unreadLabel),
                  })}
                  aria-label={ariaParts.join(', ')}
                  onClick={() => onSelectWazzupAccount(account)}
                >
                  {unreadLabel ? (
                    <span
                      className={classes.pickerUnreadBadges}
                      onMouseDown={stopBadgeEvent}
                      onClick={stopBadgeEvent}
                    >
                      <TooltipWrapper title={CRM_CHAT_UNREAD_ALL_TOOLTIP}>
                        <span className={classes.unreadBadgeHit}>
                          <VkUnreadCountBadge
                            label={unreadLabel}
                            placement="inline"
                            dock
                            variant="all"
                            aria-label={`${CRM_CHAT_UNREAD_ALL_TOOLTIP}: ${unreadCount}`}
                          />
                        </span>
                      </TooltipWrapper>
                    </span>
                  ) : null}
                  <span
                    className={classes.pickerCardIconWrap}
                    style={{ color: 'var(--color-status-success-text)' }}
                  >
                    <WazzupIcon className={classes.pickerCardIcon} />
                  </span>
                  <span className={classes.pickerCardLabel} title={title}>
                    {title}
                  </span>
                  {unreadHint ? (
                    <span className={classes.pickerCardUnreadHint}>
                      {unreadHint}
                    </span>
                  ) : null}
                  {statusHint ? (
                    <span className={classes.pickerCardHint}>
                      <LockOutlined
                        className={classes.pickerCardLock}
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                      />
                      {statusHint}
                    </span>
                  ) : null}
                </button>
              )
            })
          : null}
      </div>
    </div>
  )
}

export default IntegrationChatDockIntegrationsPicker
